// src/components/ListagemEspelho.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../supabaseClient";
import "./Listagem.css";
import "./ListagemEspelho.css";
import "./loader.css";
import { FaPaperPlane, FaPlus, FaTimes, FaFilter, FaCopy, FaCheck, FaRegCommentDots, FaEllipsisV, FaHistory } from "react-icons/fa";
import { MdOutlineSend, MdCalendarToday, MdBusiness } from "react-icons/md";
import Check from "./Check";
import Loading from "./Loading";

// ─── Larguras iniciais das colunas (px) ───────────────────────────────────────
// Removida a última coluna (Ações) que tinha largura 34
const COL_WIDTHS_DEFAULT = [34, 38, 80, 200, 56, 68, 120, 100, 140, 160];
const COL_MIN = 28;

// ─── Avatar de iniciais ───────────────────────────────────────────────────────
function AvatarIcon({ nome, variant }) {
  const inicial = (nome || "?").trim().charAt(0).toUpperCase();
  return (
    <div className={`info-bar-avatar${variant ? ` info-bar-avatar--${variant}` : ""}`}>
      {variant === "date" ? <MdCalendarToday size={15} /> : variant === "company" ? <MdBusiness size={16} /> : inicial}
    </div>
  );
}

// ─── Hook: busca histórico de pedidos de um código no projeto ─────────────────
function useHistoricoInsumo(codigo, projetoId, notaAtualId) {
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(false);
  const [descricaoInsumo, setDescricaoInsumo] = useState("");

  const buscar = useCallback(async () => {
    if (!codigo?.trim() || codigo.toLowerCase() === "criar" || !projetoId) {
      setHistorico([]);
      setDescricaoInsumo("");
      return;
    }
    setLoading(true);
    try {
      // Buscar descrição do insumo
      let descricao = "";
      try {
        const { data: insumoData } = await supabase
          .from("itens")
          .select("descricao")
          .eq("codigo", codigo.trim())
          .maybeSingle();
        if (insumoData?.descricao) {
          descricao = insumoData.descricao;
        }
      } catch (err) {
        console.warn("Erro ao buscar descrição do insumo:", err);
      }
      setDescricaoInsumo(descricao);

      let query = supabase
        .from("planilha_itens")
        .select("quantidade, locacao, criado_em, data_envio, nota_id")
        .eq("codigo", codigo.trim())
        .eq("projeto_id", projetoId)
        .order("criado_em", { ascending: false });

      if (notaAtualId) query = query.neq("nota_id", notaAtualId);

      const { data, error } = await query;
      if (error) throw error;

      // Agrupar por data + quantidade + locacao para remover duplicatas
      const mapa = new Map();
      
      (data || []).forEach((item) => {
        let locacaoArray = [];
        try {
          if (typeof item.locacao === "string") {
            const p = JSON.parse(item.locacao);
            locacaoArray = Array.isArray(p) ? p : item.locacao ? [item.locacao] : [];
          } else if (Array.isArray(item.locacao)) {
            locacaoArray = item.locacao;
          } else if (item.locacao != null) {
            locacaoArray = [String(item.locacao)];
          }
        } catch { locacaoArray = item.locacao ? [String(item.locacao)] : []; }

        const dataRef = item.data_envio || item.criado_em;
        const dataFormatada = dataRef ? new Date(dataRef).toLocaleDateString("pt-BR") : "–";
        const quantidade = item.quantidade ?? "–";
        const locacaoStr = locacaoArray.length > 0 ? locacaoArray.join(", ") : "–";
        
        const chave = `${dataFormatada}|${quantidade}|${locacaoStr}`;
        
        if (!mapa.has(chave)) {
          mapa.set(chave, {
            data: dataFormatada,
            quantidade: quantidade,
            locacao: locacaoStr,
          });
        }
      });

      const parsed = Array.from(mapa.values());
      
      parsed.sort((a, b) => {
        const dataA = a.data.split('/').reverse().join('');
        const dataB = b.data.split('/').reverse().join('');
        return dataB.localeCompare(dataA);
      });

      setHistorico(parsed);
    } catch (err) {
      console.error("Erro ao buscar histórico:", err);
      setHistorico([]);
    } finally {
      setLoading(false);
    }
  }, [codigo, projetoId, notaAtualId]);

  return { historico, loading, buscar, descricaoInsumo };
}

// ─── Popover de histórico ─────────────────────────────────────────────────────
function HistoricoPopover({ codigo, projetoId, notaAtualId, onClose }) {
  const { historico, loading, buscar, descricaoInsumo } = useHistoricoInsumo(codigo, projetoId, notaAtualId);
  const ref = useRef(null);

  useEffect(() => { buscar(); }, [buscar]);

  useEffect(() => {
    const handle = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [onClose]);

  return (
    <div ref={ref} className="historico-popover">
      <div className="historico-popover-header">
        <div className="historico-popover-title">
          <span className="historico-popover-codigo">{codigo}</span>
          {descricaoInsumo && (
            <span className="historico-popover-descricao">— {descricaoInsumo}</span>
          )}
        </div>
        <button onClick={onClose} className="historico-popover-close">
          <FaTimes size={10} />
        </button>
      </div>
      {loading ? (
        <div className="historico-popover-loading">Carregando...</div>
      ) : historico.length === 0 ? (
        <div className="historico-popover-empty">Nenhum pedido anterior encontrado.</div>
      ) : (
        <div className="historico-popover-table-wrapper">
          <table className="historico-popover-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Qtd</th>
                <th>Locação</th>
              </tr>
            </thead>
            <tbody>
              {historico.map((h, i) => (
                <tr key={i}>
                  <td>{h.data}</td>
                  <td>{h.quantidade}</td>
                  <td>
                    <span className="historico-locacao-cell" title={h.locacao}>
                      {h.locacao}
                      <span className="full-list">{h.locacao}</span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function ListagemEspelho({ projetoOrigem, notaOrigem, notaEspelhoId, onClose, onStatusUpdate }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusEnvio, setStatusEnvio] = useState(null);
  const [copiadoId, setCopiadoId] = useState(null);
  const [balaoAberto, setBalaoAberto] = useState(null);
  const balaoRef = useRef(null);

  // ─── Histórico popover ────────────────────────────────────────────────────────
  const [historicoAbertoId, setHistoricoAbertoId] = useState(null);

  // ─── Seleção de linhas para "Adicionar Insumo" ───────────────────────────────
  const [selectedCriarIds, setSelectedCriarIds] = useState(new Set());

  // ─── Info bar ────────────────────────────────────────────────────────────────
  const [infoGerador, setInfoGerador]       = useState(null);
  const [infoEnvio, setInfoEnvio]           = useState(null);
  const [infoRespondido, setInfoRespondido] = useState(null);
  const [nomeUsuarioLogado, setNomeUsuarioLogado] = useState("Usuário");
  const [userIdLogado, setUserIdLogado]     = useState("");

  // ─── Filtros ─────────────────────────────────────────────────────────────────
  const [filtros, setFiltros] = useState({ codigo: "", locacao: "", eap: "" });
  const [filtroAbertoCol, setFiltroAbertoCol] = useState(null);
  const filtroRef = useRef(null);

  // ─── Comentário popup ────────────────────────────────────────────────────────
  const [comentarioAberto, setComentarioAberto] = useState(null); // row.id
  const [comentarioTemp, setComentarioTemp] = useState("");
  const comentarioRef = useRef(null);

  // ─── Resize de colunas ───────────────────────────────────────────────────────
  const [colWidths, setColWidths] = useState(COL_WIDTHS_DEFAULT);
  const resizeRef = useRef({ active: false, colIdx: null, startX: 0, startW: 0 });

  // ─── Handlers de resize ───────────────────────────────────────────────────────
  const handleResizeMouseDown = useCallback((e, colIdx) => {
    e.preventDefault();
    resizeRef.current = { active: true, colIdx, startX: e.clientX, startW: colWidths[colIdx] };

    const onMouseMove = (ev) => {
      if (!resizeRef.current.active) return;
      const delta = ev.clientX - resizeRef.current.startX;
      const newW = Math.max(COL_MIN, resizeRef.current.startW + delta);
      setColWidths((prev) => { const next = [...prev]; next[resizeRef.current.colIdx] = newW; return next; });
    };

    const onMouseUp = () => {
      resizeRef.current.active = false;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, [colWidths]);

  // ─── Fechar filtro ao clicar fora ─────────────────────────────────────────────
  useEffect(() => {
    const handle = (e) => { if (filtroRef.current && !filtroRef.current.contains(e.target)) setFiltroAbertoCol(null); };
    if (filtroAbertoCol !== null) { document.addEventListener("mousedown", handle); return () => document.removeEventListener("mousedown", handle); }
  }, [filtroAbertoCol]);

  // ─── Fechar balão ao clicar fora ─────────────────────────────────────────────
  useEffect(() => {
    const handle = (e) => { if (balaoRef.current && !balaoRef.current.contains(e.target)) setBalaoAberto(null); };
    if (balaoAberto !== null) { document.addEventListener("mousedown", handle); return () => document.removeEventListener("mousedown", handle); }
  }, [balaoAberto]);

  // ─── Fechar comentário ao clicar fora ────────────────────────────────────────
  useEffect(() => {
    const handle = (e) => { if (comentarioRef.current && !comentarioRef.current.contains(e.target)) setComentarioAberto(null); };
    if (comentarioAberto !== null) { document.addEventListener("mousedown", handle); return () => document.removeEventListener("mousedown", handle); }
  }, [comentarioAberto]);

  // ─── Carregar usuário logado ──────────────────────────────────────────────────
  useEffect(() => {
    const fetchUserProfile = async () => {
      const { data, error } = await supabase.auth.getUser();
      if (error) return;
      const user = data.user;
      if (user) {
        const { data: profile } = await supabase.from("profiles").select("nome, id").eq("id", user.id).single();
        setNomeUsuarioLogado(profile?.nome || user.email?.split("@")[0] || "Usuário");
        setUserIdLogado(profile?.id || "");
      }
    };
    fetchUserProfile();
  }, []);

  const formatarData = (isoString) => {
    if (!isoString) return "";
    return new Date(isoString).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  const formatarTitleCase = (texto) => {
    if (!texto || typeof texto !== "string") return texto;
    return texto.toLowerCase().split(" ").map((p) => (p.length === 0 ? p : p.charAt(0).toUpperCase() + p.slice(1))).join(" ");
  };

  // ─── Função: buscar insumo por código ──────────────────────────────────────
  const buscarInsumoPorCodigo = useCallback(async (codigo) => {
    if (!codigo?.trim() || codigo.toLowerCase() === "criar") return null;
    
    try {
      const { data, error } = await supabase
        .from("itens")
        .select("codigo, descricao, unidade")
        .eq("codigo", codigo.trim())
        .maybeSingle();
      
      if (error) throw error;
      return data;
    } catch (err) {
      console.error("Erro ao buscar insumo:", err);
      return null;
    }
  }, []);

  // ─── Função para atualizar o item no banco ──────────────────────────────────
  const atualizarItemNoBanco = useCallback(async (id, codigo, descricao, unidade) => {
    try {
      // Atualiza na planilha_itens atual (espelho)
      const { error: updateError } = await supabase
        .from("planilha_itens")
        .update({ 
          codigo: codigo.trim(), 
          descricao: descricao || "", 
          unidade: unidade || "" 
        })
        .eq("id", id);
      
      if (updateError) throw updateError;

      // Atualiza também na planilha_itens original (se tiver item_original_id)
      const row = rows.find(r => r.id === id);
      if (row?.item_original_id) {
        const { error: updateOriginalError } = await supabase
          .from("planilha_itens")
          .update({ 
            codigo: codigo.trim(), 
            descricao: descricao || "", 
            unidade: unidade || "" 
          })
          .eq("id", row.item_original_id);
        
        if (updateOriginalError) throw updateOriginalError;
      }

      return { success: true };
    } catch (err) {
      console.error("Erro ao atualizar item:", err);
      return { success: false, error: err.message };
    }
  }, [rows]);

  // ─── Handler para quando o usuário pressiona Enter no campo código ──────────
  const handleCodigoKeyDown = useCallback(async (e, id) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const input = e.target;
      const codigo = input.value.trim();
      
      // Se o valor estiver vazio ou for "criar", não faz nada
      if (!codigo || codigo.toLowerCase() === "criar") {
        input.blur();
        return;
      }

      // Busca o insumo no banco
      const insumoEncontrado = await buscarInsumoPorCodigo(codigo);
      
      if (insumoEncontrado) {
        try {
          // Atualiza no banco de dados
          const result = await atualizarItemNoBanco(
            id, 
            insumoEncontrado.codigo, 
            insumoEncontrado.descricao || "", 
            insumoEncontrado.unidade || ""
          );

          if (!result.success) {
            throw new Error(result.error);
          }

          // Atualiza o estado local
          setRows(prev => prev.map(r => {
            if (r.id === id) {
              return {
                ...r,
                codigo: insumoEncontrado.codigo,
                descricao: insumoEncontrado.descricao || r.descricao,
                unidade: insumoEncontrado.unidade || r.unidade,
                isCriar: false // Marca que não é mais "criar"
              };
            }
            return r;
          }));

          // Feedback visual: linha fica verde por 1.5 segundos
          const rowElement = document.querySelector(`[data-row-id="${id}"]`);
          if (rowElement) {
            rowElement.style.transition = 'background-color 0.3s';
            rowElement.style.backgroundColor = '#e8f5e9';
            setTimeout(() => {
              rowElement.style.backgroundColor = '';
            }, 1500);
          }

          input.blur();
          
        } catch (err) {
          console.error("Erro ao salvar no banco:", err);
          // Feedback visual de erro no input
          input.style.transition = 'background-color 0.3s';
          input.style.backgroundColor = '#ffebee';
          setTimeout(() => {
            input.style.backgroundColor = '';
          }, 1500);
        }
      } else {
        // Código não encontrado - feedback visual no input
        input.style.transition = 'background-color 0.3s';
        input.style.backgroundColor = '#ffebee';
        setTimeout(() => {
          input.style.backgroundColor = '';
        }, 1500);
      }
    }
  }, [buscarInsumoPorCodigo, atualizarItemNoBanco]);

  // ─── Handler para mudança no campo código (apenas atualiza o valor) ──────────
  const handleCodigoChange = useCallback((id, valor) => {
    // Apenas atualiza o valor local, sem buscar
    setRows(prev => prev.map(r => (r.id === id ? { ...r, codigo: valor } : r)));
  }, []);

  // ─── Carregar dados ───────────────────────────────────────────────────────────
  const carregarDados = async () => {
    setLoading(true);
    try {
      if (!notaEspelhoId) { setRows([]); return; }

      const { data: notaEspelho } = await supabase
        .from("notas")
        .select("enviado_por_nome, data_envio, respondida, respondido_por_nome, created_by, created_at, nota_original_id")
        .eq("id", notaEspelhoId).single();

      if (notaEspelho) {
        let nomeGerador = null;
        if (notaEspelho.created_by) {
          const { data: perfilCriador } = await supabase.from("profiles").select("nome").eq("id", notaEspelho.created_by).single();
          nomeGerador = perfilCriador?.nome || null;
        }
        if (!nomeGerador && notaEspelho.nota_original_id) {
          const { data: notaOriginalData } = await supabase.from("notas").select("created_by, created_at").eq("id", notaEspelho.nota_original_id).single();
          if (notaOriginalData?.created_by) {
            const { data: perfilOriginal } = await supabase.from("profiles").select("nome").eq("id", notaOriginalData.created_by).single();
            nomeGerador = perfilOriginal?.nome || null;
          }
        }
        setInfoGerador(nomeGerador ? { nome: nomeGerador, data: notaEspelho.created_at } : null);
        setInfoEnvio(notaEspelho.enviado_por_nome ? { nome: notaEspelho.enviado_por_nome, data: notaEspelho.data_envio } : null);
        setInfoRespondido(notaEspelho.respondida && notaEspelho.respondido_por_nome ? { nome: notaEspelho.respondido_por_nome } : null);
      }

      const { data: itensSalvos, error } = await supabase
        .from("planilha_itens").select("*").eq("nota_id", notaEspelhoId).order("ordem", { ascending: true });

      if (error) throw error;

      const mapped = (itensSalvos || []).map((item) => {
        let locacaoArray = [];
        try {
          if (typeof item.locacao === "string") {
            const parsed = JSON.parse(item.locacao);
            locacaoArray = Array.isArray(parsed) ? parsed : item.locacao ? [item.locacao] : [];
          } else if (Array.isArray(item.locacao)) {
            locacaoArray = item.locacao;
          } else if (item.locacao != null) {
            locacaoArray = [String(item.locacao)];
          }
        } catch { locacaoArray = item.locacao ? [String(item.locacao)] : []; }

        const isCriar = (item.codigo || "").toLowerCase() === "criar";
        let descricaoFormatada = item.descricao || "";
        if (isCriar && descricaoFormatada) {
          const isUpperCase = descricaoFormatada === descricaoFormatada.toUpperCase() && descricaoFormatada !== descricaoFormatada.toLowerCase();
          if (isUpperCase) descricaoFormatada = formatarTitleCase(descricaoFormatada);
        }

        return {
          id: item.id, item_original_id: item.item_original_id,
          selecionado: Boolean(item.selecionado), codigo: item.codigo || "",
          descricao: descricaoFormatada, unidade: item.unidade || "",
          quantidade: item.quantidade || "", locacao: locacaoArray,
          eap: item.eap || "", observacao: item.observacao || "",
          comentario: item.comentario || "", criado_em: item.criado_em || null,
          grupo_envio: item.grupo_envio || "antigo",
          data_envio: item.data_envio || item.criado_em,
          enviado_por: item.enviado_por || "Usuário",
          isCriar, ordem: item.ordem || 0,
          projeto_id: item.projeto_id,
        };
      });

      setRows(mapped);
    } catch (err) {
      console.error("Erro ao carregar listagem espelho:", err);
      alert("Erro ao carregar os dados.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregarDados(); }, [notaEspelhoId]);

  // ─── Helpers ──────────────────────────────────────────────────────────────────
  const toggleSelecionado = async (id, novoValor) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, selecionado: novoValor } : r)));
    try {
      const { error } = await supabase.from("planilha_itens").update({ selecionado: novoValor }).eq("id", id);
      if (error) throw error;
    } catch (err) {
      console.error("Erro ao salvar seleção:", err);
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, selecionado: !novoValor } : r)));
      alert("Erro ao salvar a marcação.");
    }
  };

  const handleInputChange = (id, campo, valor) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [campo]: valor } : r)));
  };

  const atualizarOriginal = async (itemOriginalId, updateData) => {
    if (!itemOriginalId) return;
    try {
      const { error } = await supabase.from("planilha_itens").update(updateData).eq("id", itemOriginalId);
      if (error) console.error("Erro ao atualizar item original:", error);
    } catch (err) { console.error("Exceção ao atualizar original:", err); }
  };

  const handleComentarioSalvar = async (id) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    try {
      const updateData = { comentario: comentarioTemp || null };
      const { error } = await supabase.from("planilha_itens").update(updateData).eq("id", id);
      if (error) throw error;
      await atualizarOriginal(row.item_original_id, updateData);
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, comentario: comentarioTemp } : r)));
      setComentarioAberto(null);
    } catch (err) {
      console.error("Erro ao salvar comentário:", err);
      alert("Erro ao salvar comentário.");
    }
  };

  // ─── Handler para clicar na linha e selecionar para criação ──────────────────
  const handleRowClick = (e, row) => {
    if (!row.isCriar) return;
    if (e.target.closest("button, input, textarea, select, a, .comentario-btn-wrapper, .locacao-balao-trigger, .historico-btn")) return;
    
    setSelectedCriarIds(prev => {
      const newSet = new Set(prev);
      if (e.ctrlKey || e.metaKey) {
        if (newSet.has(row.id)) {
          newSet.delete(row.id);
        } else {
          newSet.add(row.id);
        }
      } else {
        if (newSet.has(row.id) && newSet.size === 1) {
          newSet.clear();
        } else {
          newSet.clear();
          newSet.add(row.id);
        }
      }
      return newSet;
    });
  };

  // ─── Handler para adicionar os insumos selecionados ──────────────────────────
  const handleAddSelectedCriar = async () => {
    if (selectedCriarIds.size === 0) return;
    
    const selectedRows = rows.filter(r => selectedCriarIds.has(r.id));
    let successCount = 0;
    let errorMessages = [];

    const promises = selectedRows.map(async (row) => {
      if (!row.codigo?.trim() || row.codigo.toLowerCase() === "criar") {
        return { success: false, error: `Linha "${row.descricao || row.codigo}" sem código válido.` };
      }
      try {
        const { error: insertError } = await supabase
          .from("itens")
          .insert({ 
            codigo: row.codigo.trim(), 
            descricao: row.descricao || "", 
            unidade: row.unidade || null 
          });
        
        if (insertError && insertError.code !== "23505") throw insertError;
        
        await supabase.from("planilha_itens").update({ 
          codigo: row.codigo.trim(), 
          descricao: row.descricao || "" 
        }).eq("id", row.id);
        
        await atualizarOriginal(row.item_original_id, { 
          codigo: row.codigo.trim(), 
          descricao: row.descricao || "" 
        });
        
        return { success: true, id: row.id, codigo: row.codigo.trim() };
      } catch (err) {
        console.error("Erro ao criar item:", err);
        return { success: false, error: `Erro ao criar "${row.codigo}".` };
      }
    });

    const results = await Promise.all(promises);
    
    const updatedRows = new Map();
    results.forEach(res => {
      if (res.success) {
        successCount++;
        updatedRows.set(res.id, res.codigo);
      } else {
        errorMessages.push(res.error);
      }
    });

    if (updatedRows.size > 0) {
      setRows(prev => prev.map(r => {
        if (updatedRows.has(r.id)) {
          return { ...r, codigo: updatedRows.get(r.id), isCriar: false };
        }
        return r;
      }));
    }

    setSelectedCriarIds(new Set());

    if (errorMessages.length === 0) {
      alert(`${successCount} insumo(s) adicionado(s) com sucesso! A listagem original foi sincronizada.`);
    } else {
      alert(`${successCount} insumo(s) adicionado(s).\n\nErros:\n${errorMessages.join("\n")}`);
    }
  };

  // ─── Enviar resposta ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!notaEspelhoId) return;
    setStatusEnvio("enviando");
    try {
      await Promise.all(
        rows.map(async (row) => {
          const updateData = { selecionado: row.selecionado };
          if (row.isCriar) { updateData.codigo = row.codigo?.trim() || null; updateData.descricao = row.descricao || null; }
          await supabase.from("planilha_itens").update(updateData).eq("id", row.id);
          if (row.item_original_id) await supabase.from("planilha_itens").update(updateData).eq("id", row.item_original_id);
        })
      );

      const { data: notaEspelhoData } = await supabase.from("notas").select("nota_original_id").eq("id", notaEspelhoId).single();
      if (!notaEspelhoData?.nota_original_id) throw new Error("Nota original não encontrada.");
      const notaOriginalId = notaEspelhoData.nota_original_id;

      const updateRespondida = { respondida: true, respondido_por_nome: nomeUsuarioLogado };
      await supabase.from("notas").update(updateRespondida).eq("id", notaOriginalId);
      await supabase.from("notas").update(updateRespondida).eq("id", notaEspelhoId);

      if (onStatusUpdate) {
        onStatusUpdate(notaOriginalId, { respondida: true, enviada: true });
        onStatusUpdate(notaEspelhoId,  { respondida: true, enviada: true });
      }

      setInfoRespondido({ nome: nomeUsuarioLogado });
      setStatusEnvio("sucesso");
      setTimeout(() => setStatusEnvio(null), 2000);
    } catch (err) {
      console.error("Erro ao salvar:", err);
      alert("Erro ao salvar a lista.");
      setStatusEnvio(null);
    }
  };

  // ─── Filtros ──────────────────────────────────────────────────────────────────
  const valoresUnicos = (campo) => {
    const set = new Set();
    rows.forEach((r) => {
      if (campo === "locacao") { (r.locacao || []).forEach((l) => l && set.add(l)); }
      else if (campo === "eap") { if (r.eap) set.add(r.eap); }
      else if (campo === "codigo") { if (r.codigo && r.codigo.toLowerCase() !== "criar") set.add(r.codigo); }
    });
    return [...set].sort();
  };

  const rowsFiltradas = rows.filter((r) => {
    if (filtros.codigo && r.codigo !== filtros.codigo) return false;
    if (filtros.locacao && !(r.locacao || []).includes(filtros.locacao)) return false;
    if (filtros.eap && r.eap !== filtros.eap) return false;
    return true;
  });

  const temFiltroAtivo = filtros.codigo || filtros.locacao || filtros.eap;
  const rowsParaExibir = [...rowsFiltradas].sort((a, b) => a.ordem - b.ordem);

  if (loading) {
    return <div className="listagem-card"><Loading /></div>;
  }

  // ─── Render filtro dropdown ───────────────────────────────────────────────────
  const renderFiltroDropdown = (campo, label) => (
    <>
      <span>{label}</span>
      <button
        className={`filtro-icone-btn ${filtros[campo] ? "filtro-ativo" : ""}`}
        onClick={(e) => { e.stopPropagation(); setFiltroAbertoCol(filtroAbertoCol === campo ? null : campo); }}
        title={`Filtrar ${label}`}
      >
        <FaFilter size={9} />
      </button>
      {filtroAbertoCol === campo && (
        <div ref={filtroRef} className="filtro-dropdown">
          <div
            className={`filtro-option ${filtros[campo] === "" ? "filtro-option-selected" : ""}`}
            onClick={() => { setFiltros((f) => ({ ...f, [campo]: "" })); setFiltroAbertoCol(null); }}
          >Todos</div>
          {valoresUnicos(campo).map((v) => (
            <div
              key={v}
              className={`filtro-option ${filtros[campo] === v ? "filtro-option-selected" : ""}`}
              onClick={() => { setFiltros((f) => ({ ...f, [campo]: v })); setFiltroAbertoCol(null); }}
            >{v}</div>
          ))}
        </div>
      )}
    </>
  );

  // ─── Definição das colunas ───────────────────────────────────────────────────
  const colunas = [
    { label: "",           resize: false },
    { label: "#",          resize: true,  sortIcon: true },
    { label: "Código",     resize: true,  filtro: "codigo"  },
    { label: "Descrição",  resize: true  },
    { label: "Unidade",    resize: true,  sortIcon: true },
    { label: "Qtd/pav",    resize: true,  sortIcon: true },
    { label: "Locação",    resize: true,  filtro: "locacao" },
    { label: "EAP",        resize: true,  filtro: "eap"     },
    { label: "Observação", resize: true  },
    { label: "Comentário", resize: true  },
  ];

  return (
    <div className="listagem-card">

      {/* ══ HEADER ══════════════════════════════════════════════════════════════ */}
      <div className="listagem-header-container">
        <div className="listagem-header-left">
          <div className="listagem-header-titles">
            <span className="project-name">{projetoOrigem?.name || "Sem projeto"}</span>
            <div className="sub-info">
              <span className="nota-name">{notaOrigem?.nome || "Sem nota"}</span>
            </div>
          </div>
        </div>
        {onClose && (
          <button className="listagem-close-btn" onClick={onClose} aria-label="Fechar">
            <FaTimes />
          </button>
        )}
      </div>

      {/* ══ INFO BAR ════════════════════════════════════════════════════════════ */}
      <div className="listagem-info-bar">

        {infoGerador && (
          <div className="info-bar-block">
            <AvatarIcon nome={infoGerador.nome} />
            <div className="info-bar-text">
              <span className="info-bar-label">Gerado por</span>
              <span className="info-bar-name">{infoGerador.nome}</span>
            </div>
          </div>
        )}

        {infoEnvio && (
          <div className="info-bar-block">
            <AvatarIcon nome={infoEnvio.nome} />
            <div className="info-bar-text">
              <span className="info-bar-label">Enviado por</span>
              <span className="info-bar-name">{infoEnvio.nome}</span>
            </div>
          </div>
        )}

        {infoEnvio?.data && (
          <div className="info-bar-block">
            <AvatarIcon variant="date" />
            <div className="info-bar-text">
              <span className="info-bar-label">Em</span>
              <span className="info-bar-name">{formatarData(infoEnvio.data)}</span>
            </div>
          </div>
        )}

        {infoRespondido && (
          <div className="info-bar-block">
            <AvatarIcon variant="company" />
            <div className="info-bar-text">
              <span className="info-bar-label">Respondido por</span>
              <span className="info-bar-name">{infoRespondido.nome}</span>
            </div>
          </div>
        )}

        {/* Botões de ação — alinhados à direita */}
        <div style={{ marginLeft: "auto", display: "flex", gap: "10px", alignItems: "center" }}>
          {rows.some(r => r.isCriar) && (
            <button 
              className="add-insumo-btn" 
              onClick={handleAddSelectedCriar} 
              disabled={selectedCriarIds.size === 0 || statusEnvio === "enviando"}
              title="Selecione uma ou mais linhas (Ctrl+Click para múltiplas) e clique para adicionar"
            >
              <FaPlus size={13} /> Adicionar insumo {selectedCriarIds.size > 0 ? `(${selectedCriarIds.size})` : ""}
            </button>
          )}
          
          <div className="send-action-wrapper">
            <button className="send-btn" onClick={handleSave} disabled={statusEnvio === "enviando"}>
              <FaPaperPlane size={13} /> Enviar
            </button>
            {statusEnvio === "enviando" && <span className="loader-inline" />}
            {statusEnvio === "sucesso" && <Check />}
          </div>
        </div>

      </div>

      {/* ══ FILTROS ATIVOS ══════════════════════════════════════════════════════ */}
      {temFiltroAtivo && (
        <div className="filtros-ativos-bar">
          {filtros.codigo && (
            <span className="filtro-tag">
              Código: {filtros.codigo}
              <button onClick={() => setFiltros((f) => ({ ...f, codigo: "" }))}>×</button>
            </span>
          )}
          {filtros.locacao && (
            <span className="filtro-tag">
              Locação: {filtros.locacao}
              <button onClick={() => setFiltros((f) => ({ ...f, locacao: "" }))}>×</button>
            </span>
          )}
          {filtros.eap && (
            <span className="filtro-tag">
              EAP: {filtros.eap}
              <button onClick={() => setFiltros((f) => ({ ...f, eap: "" }))}>×</button>
            </span>
          )}
          <button className="filtro-limpar-todos" onClick={() => setFiltros({ codigo: "", locacao: "", eap: "" })}>
            Limpar filtros
          </button>
        </div>
      )}

      {/* ══ TABELA ══════════════════════════════════════════════════════════════ */}
      <div className="listagem-table-wrapper">
        <table className="listagem-table" style={{ tableLayout: "fixed" }}>
          <colgroup>
            {colWidths.map((w, i) => (
              <col key={i} style={{ width: w + "px", minWidth: w + "px" }} />
            ))}
          </colgroup>

          <thead>
            <tr>
              {colunas.map((col, i) => (
                <th key={i} className={`th-resizable ${col.filtro ? "th-filtro" : ""}`}>
                  <div className="th-inner">
                    {col.filtro === "codigo"  && renderFiltroDropdown("codigo",  "Código")}
                    {col.filtro === "locacao" && renderFiltroDropdown("locacao", "Locação")}
                    {col.filtro === "eap"     && renderFiltroDropdown("eap",     "EAP")}
                    {!col.filtro && (
                      <>
                        <span>{col.label}</span>
                      </>
                    )}
                  </div>
                  {col.resize && (
                    <span className="col-resize-handle" onMouseDown={(e) => handleResizeMouseDown(e, i)} />
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rowsParaExibir.map((row, visualIdx) => {
              const nextRow = rowsParaExibir[visualIdx + 1];
              const isLastInGroup = !nextRow || nextRow.grupo_envio !== row.grupo_envio;
              const temComentario = Boolean(row.comentario?.trim());
              const temCodigo = row.codigo?.trim() && row.codigo.toLowerCase() !== "criar";

              return (
                <React.Fragment key={row.id ?? visualIdx}>
                  <tr 
                    data-row-id={row.id}
                    className={[
                      row.selecionado ? "linha-selecionada" : "",
                      selectedCriarIds.has(row.id) ? "linha-selecionada-criar" : "",
                      row.isCriar ? "linha-criar" : ""
                    ].filter(Boolean).join(" ")}
                    onClick={(e) => handleRowClick(e, row)}
                    style={{ cursor: row.isCriar ? "pointer" : "default" }}
                  >

                    {/* Checkbox */}
                    <td>
                      <input
                        type="checkbox"
                        checked={row.selecionado}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleSelecionado(row.id, e.target.checked);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>

                    {/* # */}
                    <td><span>{row.ordem}</span></td>

                    {/* Código com botão de histórico - lado a lado */}
                    <td style={{ position: "relative", overflow: "visible" }}>
                      {row.isCriar ? (
                        <input 
                          type="text" 
                          value={row.codigo} 
                          onChange={(e) => handleCodigoChange(row.id, e.target.value)}
                          onKeyDown={(e) => handleCodigoKeyDown(e, row.id)}
                          placeholder="Digite o código e pressione Enter" 
                          className="codigo-input-criar"
                          title="Digite o código e pressione Enter para buscar"
                        />
                      ) : (
                        <div className="codigo-historico-wrapper">
                          <span className="codigo-texto">{row.codigo || ""}</span>
                          {temCodigo && (
                            <button
                              className="historico-btn"
                              title={`Histórico de pedidos — ${row.codigo}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setHistoricoAbertoId(historicoAbertoId === row.id ? null : row.id);
                              }}
                            >
                              <FaHistory size={11} />
                            </button>
                          )}
                        </div>
                      )}
                      {historicoAbertoId === row.id && temCodigo && (
                        <HistoricoPopover
                          codigo={row.codigo}
                          projetoId={row.projeto_id || projetoOrigem?.id}
                          notaAtualId={notaEspelhoId}
                          onClose={() => setHistoricoAbertoId(null)}
                        />
                      )}
                    </td>

                    {/* Descrição */}
                    <td>
                      {row.isCriar ? (
                        <div className="descricao-criar-wrapper">
                          <input 
                            className="descri" 
                            type="text" 
                            value={row.descricao || ""} 
                            onChange={(e) => handleInputChange(row.id, "descricao", e.target.value)} 
                            placeholder="Descrição" 
                          />
                          <button 
                            className="copy-descricao-btn" 
                            title="Copiar descrição" 
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              navigator.clipboard.writeText(row.descricao || "").then(() => { 
                                setCopiadoId(row.id); 
                                setTimeout(() => setCopiadoId(null), 1500); 
                              }); 
                            }}
                          >
                            {copiadoId === row.id ? <FaCheck /> : <FaCopy />}
                          </button>
                        </div>
                      ) : (
                        <span>{row.descricao || ""}</span>
                      )}
                    </td>

                    {/* Unidade */}
                    <td>
                      {row.isCriar ? (
                        <input
                          type="text"
                          value={row.unidade || ""}
                          onChange={(e) => handleInputChange(row.id, "unidade", e.target.value)}
                          placeholder="Un."
                          className="unidade-input-criar"
                        />
                      ) : (
                        <span>{row.unidade || ""}</span>
                      )}
                    </td>

                    {/* Qtd/pav */}
                    <td><span>{row.quantidade || ""}</span></td>

                    {/* Locação */}
                    <td style={{ position: "relative" }}>
                      {!row.locacao?.length ? (
                        <span className="locacao-vazia">–</span>
                      ) : row.locacao.length === 1 ? (
                        <span className="locacao-unica">{row.locacao[0]}</span>
                      ) : (
                        <>
                          <button className="locacao-balao-trigger" onClick={(e) => {
                            e.stopPropagation();
                            setBalaoAberto(balaoAberto === row.id ? null : row.id);
                          }}>
                            <span className="locacao-balao-preview">{row.locacao[0]}</span>
                            <span className="locacao-balao-badge">+{row.locacao.length - 1}</span>
                          </button>
                          {balaoAberto === row.id && (
                            <div ref={balaoRef} className="locacao-balao">
                              <div className="locacao-balao-arrow" />
                              {row.locacao.map((loc, idx) => (
                                <div key={idx} className="locacao-balao-item">{loc}</div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </td>

                    {/* EAP */}
                    <td><span>{row.eap || ""}</span></td>

                    {/* Observação */}
                    <td>
                      <div className="observacao-rendered">{row.observacao || ""}</div>
                    </td>

                    {/* Comentário */}
                    <td style={{ position: "relative", overflow: "visible" }}>
                      <div className="comentario-btn-wrapper">
                        <button
                          className={`comentario-display-btn ${temComentario ? "tem-comentario" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setComentarioTemp(row.comentario || "");
                            setComentarioAberto(comentarioAberto === row.id ? null : row.id);
                          }}
                        >
                          <FaRegCommentDots size={12} />
                          {temComentario ? row.comentario : "Adicionar comentário"}
                        </button>

                        {comentarioAberto === row.id && (
                          <div ref={comentarioRef} className="comentario-popup">
                            <textarea
                              autoFocus
                              value={comentarioTemp}
                              onChange={(e) => setComentarioTemp(e.target.value)}
                              placeholder="Escrever comentário..."
                              rows={3}
                            />
                            <div className="comentario-popup-actions">
                              <button className="btn-comentario-cancelar" onClick={() => setComentarioAberto(null)}>Cancelar</button>
                              <button className="btn-comentario-salvar" onClick={() => handleComentarioSalvar(row.id)}>Salvar</button>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>

                  </tr>

                  {isLastInGroup && visualIdx < rowsParaExibir.length - 1 && (
                    <tr className="delimiter-row">
                      <td colSpan="10">
                        <div className="envio-delimiter">
                          Enviado por <strong>{row.enviado_por}</strong> em{" "}
                          {new Date(row.data_envio).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}